import { useMemo } from 'react';
import { VariableGroup } from '../variable-schema';
import './PageDiagram.scss';

type ActiveZone = VariableGroup | 'typography' | 'layout' | 'header-footer' | 'table-of-contents' | null;

interface PageDiagramProps {
  activeZone: ActiveZone;
  hoveredField: string | null;
  variables: Record<string, string>;
}

function px(val: string | undefined, fallback: number): number {
  if (!val) return fallback;
  const n = parseFloat(val);
  return isNaN(n) ? fallback : n;
}

function zoneMatch(activeZone: ActiveZone, ...zones: string[]): boolean {
  if (!activeZone) return false;
  if (zones.includes(activeZone)) return true;

  // Broad accordion → individual zones
  if (activeZone === 'typography') return zones.some((z) => z.startsWith('typography'));
  if (activeZone === 'layout') return zones.some((z) => z.startsWith('layout') || z === 'block-spacing');
  if (activeZone === 'header-footer') return zones.some((z) => z === 'header' || z === 'footer' || z === 'header-footer-text');

  return false;
}

const SPACING_OVERLAY_FIELDS = ['space-2', 'space-3', 'space-4'];

// Per-side field → side mapping for padding/margin
const sideMap: Record<string, string> = {
  wrapperPaddingTop: 'top',
  wrapperPaddingRight: 'right',
  wrapperPaddingBottom: 'bottom',
  wrapperPaddingLeft: 'left',
  wrapperMarginTop: 'top',
  wrapperMarginRight: 'right',
  wrapperMarginBottom: 'bottom',
  wrapperMarginLeft: 'left',
};

export function PageDiagram({ activeZone, hoveredField, variables }: PageDiagramProps) {
  const dims = useMemo(() => {
    const pageW = px(variables.pageWidth, 1123);
    const pageH = px(variables.pageHeight, 794);
    const diagramWidth = 200;
    const scale = diagramWidth / pageW;

    return {
      width: diagramWidth,
      height: pageH * scale,
      padT: px(variables.wrapperPaddingTop, 64) * scale,
      padR: px(variables.wrapperPaddingRight, 53) * scale,
      padB: px(variables.wrapperPaddingBottom, 54) * scale,
      padL: px(variables.wrapperPaddingLeft, 53) * scale,
      marT: px(variables.wrapperMarginTop, 22) * scale,
      marR: px(variables.wrapperMarginRight, 22) * scale,
      marB: px(variables.wrapperMarginBottom, 22) * scale,
      marL: px(variables.wrapperMarginLeft, 22) * scale,
      headerTop: px(variables.headerTop, 9) * scale,
      headerReserve: px(variables.headerReserve, 0) * scale,
      footerBottom: px(variables.footerBottom, 12) * scale,
      footerReserve: px(variables.footerReserve, 36) * scale,
      colGap: px(variables.colGap, 11) * scale,
      rowGap: px(variables.rowMarginTop, 16) * scale,
    };
  }, [variables]);

  const z = (...zones: string[]) => zoneMatch(activeZone, ...zones);

  // Per-side highlighting: if a specific field is hovered, only that side lights up.
  // If no field hovered but the group zone is active, all sides light up.
  const hoveredSide = hoveredField ? sideMap[hoveredField] ?? null : null;
  const isPadField = hoveredField?.startsWith('wrapperPadding') ?? false;
  const isMarField = hoveredField?.startsWith('wrapperMargin') ?? false;

  const padSide = (side: string) => {
    if (isPadField) return hoveredSide === side;
    if (isMarField) return false;
    return z('layout-padding');
  };
  const marSide = (side: string) => {
    if (isMarField) return hoveredSide === side;
    if (isPadField) return false;
    return z('layout-margin');
  };

  const contentTop = dims.padT + dims.marT + dims.headerReserve;
  const contentLeft = dims.padL + dims.marL;
  const contentRight = dims.padR + dims.marR;
  const contentBottom = dims.padB + dims.marB + dims.footerReserve;

  return (
    <div className="page-diagram-container">
      <div
        className={`pd ${z('paper') ? 'pd--active' : ''} ${z('paper-background') ? 'pd--bg-active' : ''} ${
          hoveredField === 'pageWidth' ? 'pd--width-active' : ''
        } ${hoveredField === 'pageHeight' ? 'pd--height-active' : ''}`}
        style={{ width: dims.width, height: dims.height }}
      >
        {/* Header strip */}
        <div
          className={`pd__strip pd__strip--header ${z('header', 'header-footer-text') ? 'pd__strip--active' : ''}`}
          style={{ top: dims.headerTop, left: dims.padL, right: dims.padR }}
        >
          <span className="pd__zone-label">Header</span>
        </div>

        {/* Padding: four edge strips highlighting the actual padding area */}
        <div className={`pd__pad pd__pad--top ${padSide('top') ? 'pd__pad--active' : ''}`} style={{ height: dims.padT + dims.headerReserve }} />
        <div className={`pd__pad pd__pad--bottom ${padSide('bottom') ? 'pd__pad--active' : ''}`} style={{ height: dims.padB + dims.footerReserve }} />
        <div
          className={`pd__pad pd__pad--left ${padSide('left') ? 'pd__pad--active' : ''}`}
          style={{ top: dims.padT + dims.headerReserve, bottom: dims.padB + dims.footerReserve, width: dims.padL }}
        />
        <div
          className={`pd__pad pd__pad--right ${padSide('right') ? 'pd__pad--active' : ''}`}
          style={{ top: dims.padT + dims.headerReserve, bottom: dims.padB + dims.footerReserve, width: dims.padR }}
        />

        {/* Margin: four edge strips inside the padding */}
        <div
          className={`pd__mar pd__mar--top ${marSide('top') ? 'pd__mar--active' : ''}`}
          style={{ top: dims.padT + dims.headerReserve, left: dims.padL, right: dims.padR, height: dims.marT }}
        />
        <div
          className={`pd__mar pd__mar--bottom ${marSide('bottom') ? 'pd__mar--active' : ''}`}
          style={{ bottom: dims.padB + dims.footerReserve, left: dims.padL, right: dims.padR, height: dims.marB }}
        />
        <div
          className={`pd__mar pd__mar--left ${marSide('left') ? 'pd__mar--active' : ''}`}
          style={{ top: dims.padT + dims.headerReserve + dims.marT, bottom: dims.padB + dims.footerReserve + dims.marB, left: dims.padL, width: dims.marL }}
        />
        <div
          className={`pd__mar pd__mar--right ${marSide('right') ? 'pd__mar--active' : ''}`}
          style={{ top: dims.padT + dims.headerReserve + dims.marT, bottom: dims.padB + dims.footerReserve + dims.marB, right: dims.padR, width: dims.marR }}
        />

        {/* Content area */}
        <div className="pd__content" style={{ top: contentTop, right: contentRight, bottom: contentBottom, left: contentLeft }}>
          {/* Two-column row with headings + body */}
          <div
            className={`pd__row ${z('layout-spacing') && !hoveredField ? 'pd__row--active' : ''} ${
              hoveredField === 'colGap' ? 'pd__row--col-gap-active' : ''
            } ${hoveredField === 'rowMarginTop' ? 'pd__row--row-gap-active' : ''}`}
            style={{ gap: dims.colGap, marginBottom: dims.rowGap, '--col-gap': `${dims.colGap}px` } as React.CSSProperties}
          >
            <div className="pd__col">
              <div className={`pd__line pd__line--h1 ${z('typography-h1') ? 'pd__line--active' : ''}`}>
                <span className="pd__line-label">H1</span>
              </div>
              <div className={`pd__line pd__line--h2 ${z('typography-h2') ? 'pd__line--active' : ''}`}>
                <span className="pd__line-label">H2</span>
              </div>
              <div className={`pd__line pd__line--p ${z('typography-body') ? 'pd__line--active' : ''}`} />
              <div className={`pd__line pd__line--p ${z('typography-body') ? 'pd__line--active' : ''}`} />
              <div className={`pd__line pd__line--h4 ${z('typography-h4') ? 'pd__line--active' : ''}`}>
                <span className="pd__line-label">H4</span>
              </div>
              <div className={`pd__line pd__line--p-short ${z('typography-body') ? 'pd__line--active' : ''}`} />
            </div>
            <div className="pd__col">
              <div className={`pd__line pd__line--h3 ${z('typography-h3') ? 'pd__line--active' : ''}`}>
                <span className="pd__line-label">H3</span>
              </div>
              <div className={`pd__line pd__line--p ${z('typography-body') ? 'pd__line--active' : ''}`} />
              <div className={`pd__line pd__line--p ${z('typography-body') ? 'pd__line--active' : ''}`} />
              <div className={`pd__line pd__line--h5 ${z('typography-h5') ? 'pd__line--active' : ''}`}>
                <span className="pd__line-label">H5</span>
              </div>
              <div className={`pd__line pd__line--h6 ${z('typography-h6') ? 'pd__line--active' : ''}`}>
                <span className="pd__line-label">H6</span>
              </div>
              <div className={`pd__line pd__line--p-short ${z('typography-body') ? 'pd__line--active' : ''}`} />
            </div>
          </div>

          {/* Table */}
          <div className={`pd__table ${z('typography-td', 'typography-th') ? 'pd__table--active' : ''}`}>
            <div className={`pd__trow pd__trow--th ${z('typography-th') ? 'pd__trow--active' : ''}`}>
              <span className="pd__line-label">TH</span>
            </div>
            <div className={`pd__trow ${z('typography-td') ? 'pd__trow--active' : ''}`}>
              <span className="pd__line-label">TD</span>
            </div>
            <div className={`pd__trow ${z('typography-td') ? 'pd__trow--active' : ''}`} />
          </div>

          {/* Unordered list */}
          <div className={`pd__list pd__list--ul ${z('typography-ul', 'typography-li') ? 'pd__list--active' : ''}`}>
            <div className="pd__li pd__li--bullet" />
            <div className="pd__li pd__li--bullet" />
          </div>

          {/* Ordered list */}
          <div className={`pd__list pd__list--ol ${z('typography-ol', 'typography-li') ? 'pd__list--active' : ''}`}>
            <div className="pd__li pd__li--number" />
            <div className="pd__li pd__li--number" />
          </div>

          {/* Overlays */}
          {z('typography-scaling') && <div className="pd__overlay pd__overlay--scaling" />}
          {(activeZone === 'block-spacing' || SPACING_OVERLAY_FIELDS.includes(hoveredField ?? '')) && <div className="pd__overlay pd__overlay--spacing" />}
        </div>

        {/* Footer strip */}
        <div
          className={`pd__strip pd__strip--footer ${z('footer', 'header-footer-text') ? 'pd__strip--active' : ''}`}
          style={{ bottom: dims.footerBottom, left: dims.padL, right: dims.padR }}
        >
          <span className="pd__zone-label">Footer</span>
        </div>
      </div>
    </div>
  );
}

export type { ActiveZone };
